import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Building2, Users, DollarSign, AlertTriangle, Calendar,
    RefreshCw, TrendingUp, TrendingDown, Plus, Edit, Trash2,
    Eye, CheckCircle, XCircle, Filter, Search, ChevronLeft, ChevronRight,
    ChevronDown, ChevronUp, History, CreditCard, Receipt, FileText, Database
} from 'lucide-react';
import styles from './AdminDashboard.module.css';

// Import API functions
import {
    getAdminDashboard,
    getBuildingPayments,
    createBuildingPayment,
    updateBuildingPayment,
    deleteBuildingPayment
} from '../../Fetch/fetchBuildingPayment';
import { getPaymentHistory as getSubscriptionPayments, getBuildingSubscription } from '../../Fetch/fetchSubscription';

import {
    getSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    validateSupplier,
    commonServiceTypes,
    cleanSupplierData
} from '../../Fetch/fetchSupplier';

import {
    listBackups,
    createBackup,
    deleteBackup,
    cleanupOldBackups,
    restoreBackup,
    getBackupStatus,
    cleanupTo14Days,
    formatFileSize,
    formatAge,
    formatDate
} from '../../Fetch/fetchBackup';

import {
    fetchBuildings,
    updateBuilding,
    validateBuilding,
    fetchSubscription
} from '../../Fetch/fetchBuilding';

// Import user API functions
import {
    getListUsers,
    getListUsersExternalDashboard,
    getAllUsersAdmin
} from '../../Fetch/fetchUser';

// Import billing components
import BillingDashboard from '../Billing/BillingDashboard';
import PaymentForm from '../Billing/PaymentForm';
import PaymentHistory from '../Billing/PaymentHistory';
import PaymentAppForm from '../Billing/PaymentAppForm';

// Note: fetchSubscription is now imported from fetchBuilding

// Subscription pricing function
const getSubscriptionPrice = (numApartments) => {
    console.log('getSubscriptionPrice called with:', numApartments, 'type:', typeof numApartments);
    
    // Handle null/undefined/empty values
    if (!numApartments) {
        console.log('No apartment count provided, returning default');
        return { price: 0, plan: 'לא זמין' };
    }
    
    // Convert to number if it's a string
    const apartmentCount = parseInt(numApartments);
    console.log('Converted to number:', apartmentCount);
    
    // Check if conversion resulted in NaN
    if (isNaN(apartmentCount)) {
        console.log('Invalid apartment count, returning default');
        return { price: 0, plan: 'לא זמין' };
    }
    
    if (apartmentCount <= 16) {
        console.log('Returning small building price: 50');
        return { price: 50, plan: 'בניין קטן' };
    } else if (apartmentCount <= 80) {
        console.log('Returning medium building price: 70');
        return { price: 70, plan: 'בניין בינוני' };
    } else {
        console.log('Returning large building price: 120');
        return { price: 120, plan: 'בניין גדול' };
    }
};

// fetchAllUsers function using the new GetAllUsers endpoint
const fetchAllUsers = async (page = 1, pageSize = 10, search = '') => {
    try {
        console.log('=== FETCHALLUSERS DEBUG (GetAllUsers API) ===');
        console.log('Calling getAllUsersAdmin API...');
        
        const response = await getAllUsersAdmin();
        console.log('getAllUsersAdmin response:', response);
        
        const allUsers = response.users || [];
        console.log('Total users from API:', allUsers.length);
        
        // Transform users data to match expected format
        const transformedUsers = allUsers.map(user => {
            console.log(`User API data:`, {
                id: user.userID,
                name: user.fullName,
                email: user.email,
                isEmailVerified: user.isEmailVerified,
                primaryBuilding: user.primaryBuilding
            });
            
            return {
                userID: user.userID,
                fullName: user.fullName,
                email: user.email,
                phone: user.phone,
                isEmailVerified: user.isEmailVerified,
                // Use primary building info for display
                buildingAddress: user.primaryBuilding?.buildingName || 'לא צוין',
                buildingCity: user.buildings?.[0]?.city || 'לא צוין',
                buildingId: user.primaryBuilding?.buildingId,
                totalBuildings: user.totalBuildings,
                totalApartments: user.totalApartments,
                isManager: user.primaryBuilding?.isManager || 'לא',
                isVaad: user.primaryBuilding?.isVaad || 'לא',
                isTenant: user.primaryBuilding?.isTenant || 'לא'
            };
        });
        
        console.log('Transformed users:', transformedUsers.length);
        
        // Check if any users have isEmailVerified field
        const hasEmailVerification = transformedUsers.some(user => user.isEmailVerified !== undefined);
        
        let filteredUsers;
        if (hasEmailVerification) {
            console.log('Email verification data available - filtering verified users');
            filteredUsers = transformedUsers.filter(user => {
                const isVerified = user.isEmailVerified === true || user.isEmailVerified === 1;
                if (!isVerified) {
                    console.log(`Filtered out user ${user.fullName} (isEmailVerified: ${user.isEmailVerified})`);
                }
                return isVerified;
            });
            console.log('After email verification filter:', filteredUsers.length, 'users');
        } else {
            console.log('⚠️ EMAIL VERIFICATION DATA NOT AVAILABLE');
            console.log('📝 Note: All users are being shown.');
            filteredUsers = transformedUsers; // Show all users if email verification data not available
        }
        
        // Apply search filter
        if (search && search.trim()) {
            const searchLower = search.toLowerCase().trim();
            console.log('Applying search filter for:', searchLower);
            const beforeSearchCount = filteredUsers.length;
            filteredUsers = filteredUsers.filter(user =>
                (user.fullName && user.fullName.toLowerCase().includes(searchLower)) ||
                (user.email && user.email.toLowerCase().includes(searchLower)) ||
                (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
                (user.buildingAddress && user.buildingAddress.toLowerCase().includes(searchLower))
            );
            console.log(`After search filter: ${filteredUsers.length} users (was ${beforeSearchCount})`);
        }

        // Apply pagination
        const totalCount = filteredUsers.length;
        const totalPages = Math.ceil(totalCount / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

        console.log(`Pagination: page ${page}, showing ${paginatedUsers.length} of ${totalCount} users`);

        return {
            items: paginatedUsers,
            totalCount,
            page,
            pageSize,
            totalPages,
            hasNextPage: page < totalPages,
            hasPreviousPage: page > 1
        };

    } catch (error) {
        console.error('Error in fetchAllUsers:', error);
        throw error;
    }
};

// Building Modal Component
const BuildingModal = ({ isOpen, onClose, building, onSave }) => {
    const [formData, setFormData] = useState({
        address: '',
        city: '',
        addressNumber: '',
        numFloors: '',
        numApartments: '',
        zipCode: '',
        createDate: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {

        if (building) {
            // Format date for input field (YYYY-MM-DD format) - FIXED VERSION
            let formattedDate = '';
            if (building.createDate) {
                const date = new Date(building.createDate);
                if (!isNaN(date.getTime())) {
                    // Use local date instead of UTC to avoid timezone issues
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    formattedDate = `${year}-${month}-${day}`;
                }
            }

            const newFormData = {
                address: building.address || '',
                city: building.city || '',
                addressNumber: (building.addressNumber || '').toString(),
                numFloors: (building.numFloors || '').toString(),
                numApartments: (building.numApartments || '').toString(),
                zipCode: building.zipCode || '',
                createDate: formattedDate
            };

            setFormData(newFormData);
        } else {
            // For new buildings, set current date as default
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            const todayFormatted = `${year}-${month}-${day}`;

            const newFormData = {
                address: '',
                city: '',
                addressNumber: '',
                numFloors: '',
                numApartments: '',
                zipCode: '',
                createDate: todayFormatted
            };

            setFormData(newFormData);
        }
        setErrors({});
    }, [building, isOpen]);

    const validateForm = () => {
        const newErrors = {};

        // Check address - handle null/undefined/empty values
        const address = formData.address || '';
        if (!address.toString().trim()) {
            newErrors.address = 'כתובת היא שדה חובה';
        }

        // Check address number - handle null/undefined/empty values
        const addressNumber = formData.addressNumber || '';
        if (!addressNumber.toString().trim()) {
            newErrors.addressNumber = 'מספר בית הוא שדה חובה';
        }

        // Check city - handle null/undefined/empty values
        const city = formData.city || '';
        if (!city.toString().trim()) {
            newErrors.city = 'עיר היא שדה חובה';
        }

        // Check floors
        const numFloors = parseInt(formData.numFloors);
        if (!formData.numFloors || isNaN(numFloors) || numFloors < 1) {
            newErrors.numFloors = 'מספר קומות חייב להיות 1 לפחות';
        }

        // Check apartments
        const numApartments = parseInt(formData.numApartments);
        if (!formData.numApartments || isNaN(numApartments) || numApartments < 1) {
            newErrors.numApartments = 'מספר דירות חייב להיות 1 לפחות';
        }

        // Check create date
        if (!formData.createDate || formData.createDate.trim() === '') {
            newErrors.createDate = 'תאריך יצירה הוא שדה חובה';
        } else {
            const selectedDate = new Date(formData.createDate);
            const today = new Date();
            today.setHours(23, 59, 59, 999); // End of today

            if (isNaN(selectedDate.getTime())) {
                newErrors.createDate = 'תאריך לא תקין';
            } else if (selectedDate > today) {
                newErrors.createDate = 'תאריך יצירה לא יכול להיות בעתיד';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Debug: Log form data before validation

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Server expects PascalCase property names as per BuildingUpdateDTO
            const buildingData = {
                Address: (formData.address || '').trim(),
                City: (formData.city || '').trim(),
                AddressNumber: parseInt(formData.addressNumber) || 0,
                NumFloors: parseInt(formData.numFloors) || 0,
                NumApartments: parseInt(formData.numApartments) || 0,
                ZipCode: (formData.zipCode || '').trim(),
                CreateDate: formData.createDate,
                // Bank account fields (optional)
                AccountNumber: null,
                BankNumber: null,
                BankName: null,
                BankAccountName: null
            };

            // Additional validation before sending
            if (!buildingData.Address) {
                throw new Error('כתובת לא יכולה להיות ריקה');
            }
            if (!buildingData.City) {
                throw new Error('עיר לא יכולה להיות ריקה');
            }


            if (building && building.buildingID) {
                const result = await updateBuilding(building.buildingID, buildingData);
            } else {
                // Note: You'll need to add a createBuilding function to your fetch functions
                // await createBuilding(buildingData);
            }

            onSave();
            onClose();
        } catch (error) {
            setErrors({ submit: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>{building ? 'עריכת בניין' : 'הוספת בניין חדש'}</h3>
                    <button onClick={onClose} className={styles.modalCloseButton}>×</button>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalForm}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>כתובת *</label>
                            <input
                                type="text"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                className={errors.address ? styles.inputError : ''}
                                placeholder="רחוב"
                            />
                            {errors.address && <span className={styles.errorText}>{errors.address}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label>מספר בית *</label>
                            <input
                                type="number"
                                value={formData.addressNumber}
                                onChange={(e) => setFormData({ ...formData, addressNumber: e.target.value })}
                                className={errors.addressNumber ? styles.inputError : ''}
                                placeholder="מספר"
                            />
                            {errors.addressNumber && <span className={styles.errorText}>{errors.addressNumber}</span>}
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>עיר *</label>
                            <input
                                type="text"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                className={errors.city ? styles.inputError : ''}
                                placeholder="עיר"
                            />
                            {errors.city && <span className={styles.errorText}>{errors.city}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label>מיקוד</label>
                            <input
                                type="text"
                                value={formData.zipCode}
                                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                                className={errors.zipCode ? styles.inputError : ''}
                                placeholder="מיקוד"
                            />
                            {errors.zipCode && <span className={styles.errorText}>{errors.zipCode}</span>}
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>מספר קומות *</label>
                            <input
                                type="number"
                                value={formData.numFloors}
                                onChange={(e) => setFormData({ ...formData, numFloors: e.target.value })}
                                className={errors.numFloors ? styles.inputError : ''}
                                placeholder="קומות"
                                min="1"
                                max="100"
                            />
                            {errors.numFloors && <span className={styles.errorText}>{errors.numFloors}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label>מספר דירות *</label>
                            <input
                                type="number"
                                value={formData.numApartments}
                                onChange={(e) => setFormData({ ...formData, numApartments: e.target.value })}
                                className={errors.numApartments ? styles.inputError : ''}
                                placeholder="דירות"
                                min="1"
                                max="1000"
                            />
                            {errors.numApartments && <span className={styles.errorText}>{errors.numApartments}</span>}
                        </div>
                    </div>

                    {/* New createDate field */}
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>תאריך יצירה *</label>
                            <input
                                type="date"
                                value={formData.createDate}
                                onChange={(e) => setFormData({ ...formData, createDate: e.target.value })}
                                className={errors.createDate ? styles.inputError : ''}
                                max={new Date().toISOString().split('T')[0]} // Prevent future dates
                            />
                            {errors.createDate && <span className={styles.errorText}>{errors.createDate}</span>}
                            <small className={styles.fieldHint}>
                                תאריך רישום הבניין במערכת
                            </small>
                        </div>
                    </div>

                    {errors.submit && (
                        <div className={styles.submitError}>{errors.submit}</div>
                    )}

                    <div className={styles.modalFooter}>
                        <button type="button" onClick={onClose} className={styles.cancelButton}>
                            ביטול
                        </button>
                        <button
                            type="submit"
                            className={styles.saveButton}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'שומר...' : (building ? 'עדכן' : 'הוסף')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Multi-Select Service Types Component
const MultiSelectServiceTypes = ({ selectedTypes, onChange, error }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownPosition, setDropdownPosition] = useState('below'); // 'below' or 'above'
    const [maxHeight, setMaxHeight] = useState('340px');
    const [optionsMaxHeight, setOptionsMaxHeight] = useState('280px');
    const searchInputRef = useRef(null);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    const handleTypeToggle = (type) => {
        const newTypes = selectedTypes.includes(type)
            ? selectedTypes.filter(t => t !== type)
            : [...selectedTypes, type];
        onChange(newTypes);
    };

    const handleSelectAll = () => {
        // Select all filtered types
        const filteredTypes = getFilteredTypes();
        const newTypes = [...new Set([...selectedTypes, ...filteredTypes])];
        onChange(newTypes);
    };

    const handleClearAll = () => {
        // Clear all filtered types
        const filteredTypes = getFilteredTypes();
        const newTypes = selectedTypes.filter(type => !filteredTypes.includes(type));
        onChange(newTypes);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleCloseDropdown = () => {
        setIsOpen(false);
        setSearchTerm(''); // Clear search when closing
    };

    const handleOpenDropdown = () => {
        setIsOpen(true);
        // Focus search input after opening (with slight delay for mobile)
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 100);
    };

    // Calculate dropdown positioning based on available space
    const calculateDropdownPosition = () => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 400; // Estimated max dropdown height including search
        
        // Fixed heights for dropdown components
        const searchSectionHeight = 80; // Search input + padding
        const actionsSectionHeight = 70; // Select All/Clear buttons + padding
        const fixedElementsHeight = searchSectionHeight + actionsSectionHeight;
        
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // Use mobile-specific positioning on small screens
        if (window.innerWidth <= 768) {
            setDropdownPosition('modal'); 
            setMaxHeight('min(70vh, 500px)');
            setOptionsMaxHeight('calc(70vh - 200px)'); // Account for fixed elements
            return;
        }
        
        // Desktop positioning logic
        let availableSpace;
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setDropdownPosition('above');
            availableSpace = Math.min(spaceAbove - 20, 500);
        } else {
            setDropdownPosition('below');
            availableSpace = Math.min(spaceBelow - 20, 500);
        }
        
        setMaxHeight(`${availableSpace}px`);
        setOptionsMaxHeight(`${Math.max(availableSpace - fixedElementsHeight, 150)}px`);
    };

    const getFilteredTypes = () => {
        if (!searchTerm.trim()) {
            return commonServiceTypes;
        }
        return commonServiceTypes.filter(type =>
            type.toLowerCase().includes(searchTerm.toLowerCase().trim())
        );
    };

    const handleDropdownClick = (e) => {
        // Prevent closing when clicking inside the dropdown
        e.stopPropagation();

        // Handle mobile close button click (pseudo-element)
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        // Check if click is in the close button area (top-right corner)
        if (clickX >= rect.width - 60 && clickY <= 60) {
            handleCloseDropdown();
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleCloseDropdown();
        }
    };

    // Effect to handle keyboard events and positioning when dropdown is open
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            // Calculate positioning after opening
            setTimeout(calculateDropdownPosition, 10);
            
            // Recalculate on window resize
            const handleResize = () => calculateDropdownPosition();
            window.addEventListener('resize', handleResize);
            
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [isOpen]);

    const filteredTypes = getFilteredTypes();
    const selectedFilteredCount = filteredTypes.filter(type => selectedTypes.includes(type)).length;

    return (
        <div className={styles.multiSelectContainer} ref={containerRef}>
            <div
                className={`${styles.multiSelectHeader} ${error ? styles.inputError : ''}`}
                onClick={handleOpenDropdown}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenDropdown();
                    }
                }}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label="בחר סוגי שירות"
            >
                <div className={styles.multiSelectValue}>
                    {selectedTypes.length === 0 ? (
                        <span className={styles.placeholder}>בחר סוגי שירות</span>
                    ) : (
                        <span>{selectedTypes.length} נבחרו: {selectedTypes.slice(0, 2).join(', ')}{selectedTypes.length > 2 ? '...' : ''}</span>
                    )}
                </div>
                <div className={styles.multiSelectArrow}>
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </div>

            {isOpen && (
                <>
                    {dropdownPosition === 'modal' && (
                        <div 
                            className={`${styles.multiSelectOverlay} ${styles.modalOverlay}`}
                            onClick={handleCloseDropdown}
                            aria-hidden="true"
                        />
                    )}
                    <div
                        ref={dropdownRef}
                        className={`${styles.multiSelectDropdown} ${
                            dropdownPosition === 'above' ? styles.positionAbove : ''
                        } ${
                            dropdownPosition === 'modal' ? styles.modalPosition : ''
                        }`}
                        style={{ maxHeight }}
                        onClick={handleDropdownClick}
                        role="listbox"
                        aria-label="רשימת סוגי שירות"
                    >
                    <div className={styles.multiSelectActions}>
                        <button
                            type="button"
                            onClick={handleSelectAll}
                            className={styles.selectAllButton}
                            title={searchTerm ? `בחר הכל (${filteredTypes.length} מתוך ${commonServiceTypes.length})` : 'בחר הכל'}
                            disabled={filteredTypes.length === 0}
                            aria-label={`בחר את כל ${filteredTypes.length} סוגי השירות המוצגים`}
                        >
                            בחר הכל {searchTerm && `(${filteredTypes.length})`}
                        </button>
                        <button
                            type="button"
                            onClick={handleClearAll}
                            className={styles.clearAllButton}
                            title={searchTerm ? `נקה הכל (${selectedFilteredCount} נבחרו)` : 'נקה הכל'}
                            disabled={selectedFilteredCount === 0}
                            aria-label={`נקה את ${selectedFilteredCount} סוגי השירות שנבחרו`}
                        >
                            נקה הכל {searchTerm && selectedFilteredCount > 0 && `(${selectedFilteredCount})`}
                        </button>
                    </div>

                    {/* Search Input */}
                    <div className={styles.multiSelectSearch}>
                        <div className={styles.searchInputWrapper}>
                            <Search size={14} className={styles.searchIcon} />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="חפש סוג שירות..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className={styles.searchInput}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="חפש בסוגי שירות"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchTerm('');
                                        if (searchInputRef.current) {
                                            searchInputRef.current.focus();
                                        }
                                    }}
                                    className={styles.clearSearchButton}
                                    title="נקה חיפוש"
                                    aria-label="נקה חיפוש"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                        {searchTerm && (
                            <div className={styles.searchResultsInfo} aria-live="polite">
                                {filteredTypes.length > 0
                                    ? `נמצאו ${filteredTypes.length} תוצאות מתוך ${commonServiceTypes.length}`
                                    : 'לא נמצאו תוצאות'
                                }
                            </div>
                        )}
                    </div>

                    <div 
                        className={styles.multiSelectOptions} 
                        style={{ maxHeight: optionsMaxHeight }}
                        role="group" 
                        aria-label="רשימת סוגי שירות"
                    >
                        {filteredTypes.length > 0 ? (
                            filteredTypes.map(type => (
                                <label
                                    key={`service-${type}`}
                                    className={styles.multiSelectOption}
                                    role="option"
                                    aria-selected={selectedTypes.includes(type)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedTypes.includes(type)}
                                        onChange={() => handleTypeToggle(type)}
                                        aria-label={`${type} - ${selectedTypes.includes(type) ? 'נבחר' : 'לא נבחר'}`}
                                    />
                                    <span className={styles.multiSelectOptionText}>{type}</span>
                                </label>
                            ))
                        ) : (
                            <div className={styles.noResults} role="status" aria-live="polite">
                                <div className={styles.noResultsIcon}>🔍</div>
                                <div className={styles.noResultsText}>
                                    לא נמצאו סוגי שירות התואמים לחיפוש "{searchTerm}"
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchTerm('');
                                        if (searchInputRef.current) {
                                            searchInputRef.current.focus();
                                        }
                                    }}
                                    className={styles.clearSearchInlineButton}
                                    aria-label="נקה חיפוש וחזור לרשימה המלאה"
                                >
                                    נקה חיפוש
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                </>
            )}

            {/* Close dropdown when clicking outside - only for non-modal positioning */}
            {isOpen && dropdownPosition !== 'modal' && (
                <div
                    className={styles.multiSelectOverlay}
                    onClick={handleCloseDropdown}
                    aria-hidden="true"
                />
            )}
        </div>
    );
};

// Supplier Modal Component with Multi-Select and Priority - FIXED EMAIL VALIDATION
const SupplierModal = ({ isOpen, onClose, supplier, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        contactPerson: '',
        phone: '',
        email: '',
        serviceTypes: [], // Changed to array for multiple selection
        priority: 10 // New priority field with default value
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (supplier) {
            // Convert serviceType string back to array
            const serviceTypesArray = supplier.serviceType
                ? supplier.serviceType.split(';').map(type => type.trim()).filter(type => type)
                : [];

            setFormData({
                name: supplier.name || '',
                contactPerson: supplier.contactPerson || '',
                phone: supplier.phone || '',
                email: supplier.email || '',
                serviceTypes: serviceTypesArray,
                priority: supplier.priority || 10
            });
        } else {
            setFormData({
                name: '',
                contactPerson: '',
                phone: '',
                email: '',
                serviceTypes: [],
                priority: 10
            });
        }
        setErrors({});
    }, [supplier, isOpen]);

    const handleServiceTypesChange = (newTypes) => {
        setFormData({ ...formData, serviceTypes: newTypes });
    };

    // FIXED validation function - EMAIL IS NOW TRULY OPTIONAL
    const validateSupplierForm = (supplierData) => {
        const errors = {};

        if (!supplierData.name || supplierData.name.trim().length === 0) {
            errors.name = 'שם הספק חובה';
        } else if (supplierData.name.length > 200) {
            errors.name = 'שם הספק לא יכול להיות יותר מ-200 תווים';
        }

        if (supplierData.contactPerson && supplierData.contactPerson.length > 200) {
            errors.contactPerson = 'שם איש הקשר לא יכול להיות יותר מ-200 תווים';
        }

        if (supplierData.phone && supplierData.phone.length > 20) {
            errors.phone = 'מספר הטלפון לא יכול להיות יותר מ-20 תווים';
        }

        // EMAIL IS NOW TRULY OPTIONAL - Only validate if not empty after trimming
        if (supplierData.email && supplierData.email.trim() !== '') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(supplierData.email.trim())) {
                errors.email = 'כתובת האימייל לא תקינה';
            } else if (supplierData.email.length > 200) {
                errors.email = 'כתובת האימייל לא יכולה להיות יותר מ-200 תווים';
            }
        }

        if (supplierData.serviceTypes && supplierData.serviceTypes.length === 0) {
            errors.serviceTypes = 'יש לבחור לפחות סוג שירות אחד';
        }

        if (supplierData.priority < 1 || supplierData.priority > 99) {
            errors.priority = 'עדיפות חייבת להיות בין 1 ל-99';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validation = validateSupplierForm(formData);
        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        setIsSubmitting(true);
        try {
            // Prepare data for API - convert serviceTypes array to string
            const rawSupplierData = {
                name: formData.name,
                contactPerson: formData.contactPerson,
                phone: formData.phone,
                email: formData.email,
                serviceType: formData.serviceTypes.join('; '), // Join with semicolon for database
                priority: parseInt(formData.priority)
            };

            // Use the cleanSupplierData function to properly handle empty email
            const cleanedSupplierData = cleanSupplierData(rawSupplierData);

            if (supplier) {
                await updateSupplier(supplier.supplierID, cleanedSupplierData);
            } else {
                await createSupplier(cleanedSupplierData);
            }
            onSave();
            onClose();
        } catch (error) {
            setErrors({ submit: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>{supplier ? 'עריכת ספק' : 'הוספת ספק חדש'}</h3>
                    <button onClick={onClose} className={styles.modalCloseButton}>×</button>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalForm}>
                    <div className={styles.formGroup}>
                        <label>שם הספק *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className={errors.name ? styles.inputError : ''}
                            placeholder="הכנס שם ספק"
                        />
                        {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                    </div>

                    <div className={styles.formGroup}>
                        <label>איש קשר</label>
                        <input
                            type="text"
                            value={formData.contactPerson}
                            onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                            className={errors.contactPerson ? styles.inputError : ''}
                            placeholder="שם איש הקשר"
                        />
                        {errors.contactPerson && <span className={styles.errorText}>{errors.contactPerson}</span>}
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>טלפון</label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                className={errors.phone ? styles.inputError : ''}
                                placeholder="מספר טלפון"
                            />
                            {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                        </div>
                        <div className={styles.formGroup}>
                            <label>אימייל (אופציונלי)</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className={errors.email ? styles.inputError : ''}
                                placeholder="כתובת אימייל (לא חובה)"
                            />
                            {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                            <small className={styles.fieldHint}>
                                שדה זה הוא אופציונלי - ניתן להשאיר ריק
                            </small>
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>סוגי שירות *</label>
                            <MultiSelectServiceTypes
                                selectedTypes={formData.serviceTypes}
                                onChange={handleServiceTypesChange}
                                error={errors.serviceTypes}
                            />
                            {errors.serviceTypes && <span className={styles.errorText}>{errors.serviceTypes}</span>}

                            {/* Display selected types */}
                            {formData.serviceTypes.length > 0 && (
                                <div className={styles.selectedTypesDisplay}>
                                    <small>נבחרו: {formData.serviceTypes.join(', ')}</small>
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label>
                                עדיפות (1-99) *
                                <span className={styles.priorityHint}>מספר נמוך = עדיפות גבוהה</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="99"
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 10 })}
                                className={errors.priority ? styles.inputError : ''}
                                placeholder="10"
                            />
                            {errors.priority && <span className={styles.errorText}>{errors.priority}</span>}
                            <small className={styles.fieldHint}>
                                ספקים עם מספר עדיפות נמוך יותר יוצגו ראשונים במשימות
                            </small>
                        </div>
                    </div>

                    {errors.submit && (
                        <div className={styles.submitError}>{errors.submit}</div>
                    )}

                    <div className={styles.modalFooter}>
                        <button type="button" onClick={onClose} className={styles.cancelButton}>
                            ביטול
                        </button>
                        <button
                            type="submit"
                            className={styles.saveButton}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'שומר...' : (supplier ? 'עדכן' : 'הוסף')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const getPageNumbers = () => {
        const delta = 2;
        const range = [];
        const rangeWithDots = [];

        // Calculate the range of pages around current page
        for (let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++) {
            range.push(i);
        }

        // Always add page 1 first
        rangeWithDots.push(1);

        // Add dots and range if needed
        if (currentPage - delta > 2) {
            rangeWithDots.push('...');
        }

        // Add the range, but skip page 1 if it's already included
        range.forEach(page => {
            if (page !== 1 && page !== totalPages) {
                rangeWithDots.push(page);
            }
        });

        // Add dots and last page if needed
        if (currentPage + delta < totalPages - 1) {
            rangeWithDots.push('...');
        }

        // Add last page only if it's different from page 1 and not already included
        if (totalPages > 1 && !rangeWithDots.includes(totalPages)) {
            rangeWithDots.push(totalPages);
        }

        return rangeWithDots;
    };

    if (totalPages <= 1) return null;

    return (
        <div className={styles.pagination}>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={styles.paginationButton}
            >
                <ChevronRight size={16} />
                הקודם
            </button>

            {getPageNumbers().map((pageNumber, index) => (
                <button
                    key={typeof pageNumber === 'number' ? `page-${pageNumber}` : `dots-${index}`}
                    onClick={() => typeof pageNumber === 'number' && onPageChange(pageNumber)}
                    className={`${styles.paginationButton} ${pageNumber === currentPage ? styles.paginationButtonActive : ''
                        } ${typeof pageNumber !== 'number' ? styles.paginationDots : ''}`}
                    disabled={typeof pageNumber !== 'number'}
                >
                    {pageNumber}
                </button>
            ))}

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
            >
                הבא
                <ChevronLeft size={16} />
            </button>
        </div>
    );
};

// Buildings Tab Component
const BuildingsTab = () => {
    const [buildings, setBuildings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const pageSize = 10;

    const loadBuildings = async () => {
        setIsLoading(true);
        try {
            const result = await fetchBuildings(currentPage, pageSize, search);
            setBuildings(result.items);
            setTotalPages(result.totalPages);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadBuildings();
    }, [currentPage, search]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const handleEditBuilding = (building) => {
        setSelectedBuilding(building);
        setIsModalOpen(true);
    };

    const handleModalSave = () => {
        setIsModalOpen(false);
        loadBuildings();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '-';

            // Format as DD/MM/YYYY
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            return `${day}/${month}/${year}`;
        } catch (error) {
            return '-';
        }
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
                <h3>רשימת בניינים</h3>
                <div className={styles.searchBox}>
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="חפש בניין..."
                        value={search}
                        onChange={handleSearch}
                    />
                </div>
            </div>

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <RefreshCw className={styles.loadingIcon} />
                    <p>טוען בניינים...</p>
                </div>
            ) : (
                <>
                    <div className={styles.tableContainer}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>כתובת</th>
                                    <th>עיר</th>
                                    <th>קומות</th>
                                    <th>דירות</th>
                                    <th>מחיר מנוי</th>
                                    <th>מיקוד</th>
                                    <th>תאריך יצירה</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buildings.map(building => {
                                    console.log('Building data:', building);
                                    const { price, plan } = getSubscriptionPrice(building.numApartments);
                                    return (
                                    <tr key={building.buildingID}>
                                        <td className={styles.buildingAddress}>
                                            {building.address} {building.addressNumber}
                                        </td>
                                        <td>{building.city}</td>
                                        <td>{building.numFloors}</td>
                                        <td>{building.numApartments}</td>
                                        <td className={styles.priceCell}>
                                            <div className={styles.priceInfo}>
                                                <strong>₪{price}</strong>
                                                <small>{plan}</small>
                                            </div>
                                        </td>
                                        <td>{building.zipCode || '-'}</td>
                                        <td className={styles.dateCell}>
                                            {formatDate(building.createDate)}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleEditBuilding(building)}
                                                className={styles.editActionButton}
                                                title="ערוך"
                                            >
                                                <Edit size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                                {buildings.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className={styles.emptyCell}>
                                            אין בניינים להצגה
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}

            <BuildingModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                building={selectedBuilding}
                onSave={handleModalSave}
            />
        </div>
    );
};

// Suppliers Tab Component
const SuppliersTab = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const pageSize = 10;

    const loadSuppliers = async () => {
        setIsLoading(true);
        try {
            const result = await getSuppliers(currentPage, pageSize, search);
            setSuppliers(result.items);
            setTotalPages(result.totalPages);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSuppliers();
    }, [currentPage, search]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const handleAddSupplier = () => {
        setSelectedSupplier(null);
        setIsModalOpen(true);
    };

    const handleEditSupplier = (supplier) => {
        setSelectedSupplier(supplier);
        setIsModalOpen(true);
    };

    const handleDeleteSupplier = async (supplier) => {
        if (window.confirm(`האם אתה בטוח שברצונך למחוק את הספק "${supplier.name}"?`)) {
            try {
                await deleteSupplier(supplier.supplierID);
                loadSuppliers();
            } catch (error) {
                alert('שגיאה במחיקת הספק: ' + error.message);
            }
        }
    };

    const handleModalSave = () => {
        setIsModalOpen(false);
        loadSuppliers();
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
                <h3>רשימת ספקים</h3>
                <div className={styles.tabHeaderActions}>
                    <div className={styles.searchBox}>
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="חפש ספק..."
                            value={search}
                            onChange={handleSearch}
                        />
                    </div>
                    <button onClick={handleAddSupplier} className={styles.addButton}>
                        <Plus size={16} />
                        הוסף ספק
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <RefreshCw className={styles.loadingIcon} />
                    <p>טוען ספקים...</p>
                </div>
            ) : (
                <>
                    <div className={styles.tableContainer}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>שם הספק</th>
                                    <th>איש קשר</th>
                                    <th>טלפון</th>
                                    <th>אימייל</th>
                                    <th>סוגי שירות</th>
                                    <th>עדיפות</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {suppliers.map(supplier => (
                                    <tr key={supplier.supplierID}>
                                        <td className={styles.supplierName}>{supplier.name}</td>
                                        <td>{supplier.contactPerson || '-'}</td>
                                        <td>{supplier.phone || '-'}</td>
                                        <td>{supplier.email || '-'}</td>
                                        <td className={styles.serviceTypesCell}>
                                            {supplier.serviceType ? (
                                                <div className={styles.serviceTypesList}>
                                                    {supplier.serviceType.split(';').map((type, index) => (
                                                        <span key={`${supplier.supplierID}-service-${index}`} className={styles.serviceTypeTag}>
                                                            {type.trim()}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            <span className={`${styles.priorityBadge} ${supplier.priority <= 3 ? styles.highPriority : supplier.priority <= 6 ? styles.mediumPriority : styles.lowPriority}`}>
                                                {supplier.priority}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.actionButtons}>
                                                <button
                                                    onClick={() => handleEditSupplier(supplier)}
                                                    className={styles.editActionButton}
                                                    title="ערוך"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteSupplier(supplier)}
                                                    className={styles.deleteActionButton}
                                                    title="מחק"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {suppliers.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className={styles.emptyCell}>
                                            אין ספקים להצגה
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}

            <SupplierModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                supplier={selectedSupplier}
                onSave={handleModalSave}
            />
        </div>
    );
};

// Users Tab Component - FIXED VERSION
const UsersTab = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState('');

    // Debug users state changes
    React.useEffect(() => {
        console.log('=== USERS STATE DEBUG ===');
        console.log('Users array length:', users.length);
        console.log('Users data:', users);
        users.forEach((user, index) => {
            console.log(`User ${index}:`, {
                id: user.userID,
                name: user.fullName,
                email: user.email,
                isEmailVerified: user.isEmailVerified,
                buildingAddress: user.buildingAddress,
                buildingCity: user.buildingCity
            });
        });
    }, [users]);
    const [error, setError] = useState(null);
    const pageSize = 10;

    const loadUsers = async () => {
        console.log('=== LOAD USERS DEBUG ===');
        console.log('Starting loadUsers - Page:', currentPage, 'Search:', search);
        setIsLoading(true);
        setError(null);
        try {
            console.log('Calling fetchAllUsers...');
            const result = await fetchAllUsers(currentPage, pageSize, search);
            console.log('fetchAllUsers result:', result);

            console.log('Setting users state with:', result.items?.length || 0, 'users');
            setUsers(result.items || []);
            setTotalPages(result.totalPages || 0);
            setTotalCount(result.totalCount || 0);
            
            console.log('Users state updated - Total count:', result.totalCount, 'Total pages:', result.totalPages);
        } catch (error) {
            console.error('Error in loadUsers:', error);
            setError('שגיאה בטעינת רשימת המשתמשים: ' + error.message);
            setUsers([]);
            setTotalPages(0);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
            console.log('loadUsers completed');
        }
    };

    useEffect(() => {
        loadUsers();
    }, [currentPage, search]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
                <h3>רשימת משתמשים ({totalCount})</h3>
                <div className={styles.tabHeaderActions}>
                    <div className={styles.searchBox}>
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="חפש משתמש"
                            value={search}
                            onChange={handleSearch}
                        />
                    </div>
                    <button
                        onClick={loadUsers}
                        className={styles.refreshButton}
                        disabled={isLoading}
                        title="רענן רשימה"
                    >
                        <RefreshCw size={16} className={isLoading ? styles.rotating : ''} />
                        רענן
                    </button>
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <AlertTriangle size={16} />
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <RefreshCw className={styles.loadingIcon} />
                    <p>טוען משתמשים מכל הבניינים...</p>
                </div>
            ) : (
                <>
                    <div className={styles.tableContainer}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>שם מלא</th>
                                    <th>אימייל</th>
                                    <th>טלפון</th>
                                    <th>מספר דירה</th>
                                    <th>בניין</th>
                                    <th>עיר</th>
                                    <th>תאריך הצטרפות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user, index) => {
                                    // Create a truly unique key by combining multiple identifiers
                                    const uniqueKey = user.userID && user.buildingId
                                        ? `user-${user.userID}-building-${user.buildingId}`
                                        : `user-${index}-building-${user.buildingId || 'unknown'}`;

                                    return (
                                        <tr key={uniqueKey}>
                                            <td className={styles.userName}>
                                                {user.fullName || user.FullName || '-'}
                                            </td>
                                            <td>{user.email || user.Email || '-'}</td>
                                            <td>{user.phone || user.Phone || '-'}</td>
                                            <td>{user.apartmentNumber || user.ApartmentNumber || '-'}</td>
                                            <td className={styles.buildingInfo}>
                                                {user.buildingAddress || '-'}
                                            </td>
                                            <td>{user.buildingCity || '-'}</td>
                                            <td>
                                                {user.createdDate || user.CreatedDate
                                                    ? (() => {
                                                        const date = new Date(user.createdDate || user.CreatedDate);
                                                        if (isNaN(date.getTime())) return '-';
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const year = date.getFullYear();
                                                        return `${day}/${month}/${year}`;
                                                    })()
                                                    : '-'
                                                }
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </>
            )}
        </div>
    );
};

// NEW: Payment History Tab Component
const PaymentHistoryTab = () => {
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [search, setSearch] = useState('');
    const [error, setError] = useState(null);

    const monthNames = [
        'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
        'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
    ];

    const loadPaymentHistory = async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Get all buildings info first
            const buildingsResponse = await fetchBuildings(1, 1000);
            const buildings = buildingsResponse.items || [];

            // Create a map of building info for quick lookup
            const buildingMap = {};
            buildings.forEach(building => {
                buildingMap[building.buildingID] = {
                    address: building.address,
                    addressNumber: building.addressNumber,
                    city: building.city
                };
            });

            // Group payments by building and year
            const historyMap = {};

            // Fetch subscription payments and subscription info for each building
            for (const building of buildings) {
                try {
                    // Fetch both payment history and subscription info
                    const [paymentResponse, subscriptionResponse] = await Promise.all([
                        getSubscriptionPayments(building.buildingID),
                        getBuildingSubscription(building.buildingID).catch(() => null)
                    ]);
                    
                    if (paymentResponse?.success && paymentResponse?.payments) {
                        const payments = paymentResponse.payments;
                        const subscription = subscriptionResponse?.success ? subscriptionResponse.data : null;
                        
                        payments.forEach(payment => {
                            // Extract date parts from payment date
                            const paymentDate = new Date(payment.paymentDate);
                            const year = paymentDate.getFullYear();
                            const month = paymentDate.getMonth() + 1; // JavaScript months are 0-based
                            
                            // Initialize building entry for this year if not exists
                            const key = `${building.buildingID}-${year}`;
                            if (!historyMap[key]) {
                                const buildingInfo = buildingMap[building.buildingID] || {};
                                historyMap[key] = {
                                    buildingId: building.buildingID,
                                    year: year,
                                    buildingAddress: buildingInfo.address || 'לא ידוע',
                                    addressNumber: buildingInfo.addressNumber || '',
                                    city: buildingInfo.city || 'לא ידוע',
                                    payments: Array(12).fill(null) // Initialize 12 months
                                };
                            }
                            
                            // Store the actual payment in the correct month
                            if (month >= 1 && month <= 12) {
                                historyMap[key].payments[month - 1] = {
                                    amount: payment.amount,
                                    date: payment.paymentDate,
                                    id: payment.paymentAppId,
                                    status: payment.paymentStatus,
                                    method: payment.paymentMethod,
                                    isActualPayment: true
                                };
                            }
                            
                            // Check if this payment covers multiple months (using NextPaymentDue)
                            if (subscription?.nextPaymentDue) {
                                const nextPaymentDue = new Date(subscription.nextPaymentDue);
                                const paymentEndMonth = nextPaymentDue.getMonth(); // 0-based
                                const paymentEndYear = nextPaymentDue.getFullYear();
                                
                                // Calculate how many months this payment covers
                                let currentDate = new Date(paymentDate);
                                currentDate.setDate(1); // Start of the payment month
                                
                                while (currentDate < nextPaymentDue) {
                                    const currentYear = currentDate.getFullYear();
                                    const currentMonth = currentDate.getMonth() + 1; // Convert to 1-based
                                    
                                    // Skip the original payment month (already handled above)
                                    if (!(currentYear === year && currentMonth === month)) {
                                        const currentKey = `${building.buildingID}-${currentYear}`;
                                        
                                        // Initialize year entry if needed
                                        if (!historyMap[currentKey]) {
                                            const buildingInfo = buildingMap[building.buildingID] || {};
                                            historyMap[currentKey] = {
                                                buildingId: building.buildingID,
                                                year: currentYear,
                                                buildingAddress: buildingInfo.address || 'לא ידוע',
                                                addressNumber: buildingInfo.addressNumber || '',
                                                city: buildingInfo.city || 'לא ידוע',
                                                payments: Array(12).fill(null)
                                            };
                                        }
                                        
                                        // Only mark as covered if there's no actual payment for this month
                                        if (!historyMap[currentKey].payments[currentMonth - 1]) {
                                            historyMap[currentKey].payments[currentMonth - 1] = {
                                                amount: payment.amount, // Show the original payment amount
                                                date: payment.paymentDate, // Reference to original payment date
                                                id: payment.paymentAppId,
                                                status: 'Covered',
                                                method: payment.paymentMethod,
                                                isActualPayment: false,
                                                coveredByPayment: true,
                                                originalPaymentDate: payment.paymentDate
                                            };
                                        }
                                    }
                                    
                                    // Move to next month
                                    currentDate.setMonth(currentDate.getMonth() + 1);
                                }
                            }
                        });
                    }
                } catch (error) {
                    // Continue with other buildings even if one fails
                }
            }

            // Convert to array and filter by year and search
            let historyArray = Object.values(historyMap);

            // Filter by selected year
            historyArray = historyArray.filter(item => item.year === selectedYear);

            // Filter by search term
            if (search && search.trim()) {
                const searchLower = search.toLowerCase().trim();
                historyArray = historyArray.filter(item =>
                    item.buildingAddress.toLowerCase().includes(searchLower) ||
                    item.city.toLowerCase().includes(searchLower) ||
                    item.addressNumber.toString().includes(searchLower)
                );
            }

            // Sort by building address
            historyArray.sort((a, b) => {
                const addressA = `${a.buildingAddress} ${a.addressNumber}`;
                const addressB = `${b.buildingAddress} ${b.addressNumber}`;
                return addressA.localeCompare(addressB, 'he');
            });

            setPaymentHistory(historyArray);
        } catch (error) {
            setError('שגיאה בטעינת היסטוריית תשלומים: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadPaymentHistory();
    }, [selectedYear, search]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
    };

    const handleYearChange = (e) => {
        setSelectedYear(parseInt(e.target.value));
    };

    // Generate year options (current year ± 5 years)
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let year = currentYear + 2; year >= currentYear - 5; year--) {
        yearOptions.push(year);
    }

    const formatAmount = (amount) => {
        if (!amount) return '';
        return `₪${amount.toLocaleString()}`;
    };

    const getPaymentCellClass = (payment) => {
        if (!payment) return styles.emptyPayment;
        if (payment.coveredByPayment) return styles.coveredPayment;
        return styles.paidPayment;
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
                <h3>היסטוריית תשלומים - {selectedYear}</h3>
                <div className={styles.tabHeaderActions}>
                    <div className={styles.searchBox}>
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="חפש בניין..."
                            value={search}
                            onChange={handleSearch}
                        />
                    </div>

                    <select
                        value={selectedYear}
                        onChange={handleYearChange}
                        className={styles.yearSelect}
                    >
                        {yearOptions.map(year => (
                            <option key={`year-${year}`} value={year}>{year}</option>
                        ))}
                    </select>

                    <button
                        onClick={loadPaymentHistory}
                        className={styles.refreshButton}
                        disabled={isLoading}
                        title="רענן נתונים"
                    >
                        <RefreshCw size={16} className={isLoading ? styles.rotating : ''} />
                        רענן
                    </button>
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <AlertTriangle size={16} />
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <RefreshCw className={styles.loadingIcon} />
                    <p>טוען היסטוריית תשלומים...</p>
                </div>
            ) : (
                <div className={styles.paymentHistoryContainer}>
                    <div className={styles.paymentHistoryTable}>
                        <table className={styles.historyTable}>
                            <thead>
                                <tr>
                                    <th className={styles.buildingColumn}>בניין</th>
                                    {monthNames.map((month, index) => (
                                        <th key={`month-${index}`} className={styles.monthColumn}>
                                            {month}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paymentHistory.map((building, index) => (
                                    <tr key={`${building.buildingId}-${building.year}`}>
                                        <td className={styles.buildingCell}>
                                            <div className={styles.buildingInfo}>
                                                <strong>
                                                    {building.buildingAddress} {building.addressNumber}
                                                </strong>
                                                <br />
                                                <small>{building.city}</small>
                                            </div>
                                        </td>
                                        {building.payments.map((payment, monthIndex) => (
                                            <td
                                                key={monthIndex}
                                                className={getPaymentCellClass(payment)}
                                                title={payment ?
                                                    payment.coveredByPayment ?
                                                        `מכוסה על ידי תשלום מתאריך ${(() => {
                                                            const date = new Date(payment.originalPaymentDate);
                                                            if (isNaN(date.getTime())) return 'תאריך לא תקין';
                                                            const day = String(date.getDate()).padStart(2, '0');
                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                            const year = date.getFullYear();
                                                            return `${day}/${month}/${year}`;
                                                        })()} - סכום: ${formatAmount(payment.amount)}` :
                                                        `שולם: ${formatAmount(payment.amount)} בתאריך ${(() => {
                                                            const date = new Date(payment.date);
                                                            if (isNaN(date.getTime())) return 'תאריך לא תקין';
                                                            const day = String(date.getDate()).padStart(2, '0');
                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                            const year = date.getFullYear();
                                                            return `${day}/${month}/${year}`;
                                                        })()}` :
                                                    'לא שולם'
                                                }
                                            >
                                                {payment ? (
                                                    <div className={styles.paymentInfo}>
                                                        <CheckCircle 
                                                            size={16} 
                                                            className={payment.coveredByPayment ? styles.coveredIcon : styles.paidIcon} 
                                                        />
                                                        <div className={styles.amountText}>
                                                            {payment.coveredByPayment ? (
                                                                <span title="מכוסה על ידי תשלום קודם">✓</span>
                                                            ) : (
                                                                formatAmount(payment.amount)
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className={styles.emptyPaymentInfo}>
                                                        <XCircle size={16} className={styles.unpaidIcon} />
                                                    </div>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {paymentHistory.length === 0 && (
                                    <tr>
                                        <td colSpan={13} className={styles.emptyCell}>
                                            {search ?
                                                'לא נמצאו בניינים התואמים את החיפוש' :
                                                `אין נתוני תשלומים עבור שנת ${selectedYear}`
                                            }
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Summary */}
                    {paymentHistory.length > 0 && (
                        <div className={styles.historySummary}>
                            <h4>סיכום שנתי</h4>
                            <div className={styles.summaryStats}>
                                <div className={styles.summaryStat}>
                                    <span className={styles.statLabel}>בניינים במערכת:</span>
                                    <span className={styles.statValue}>{paymentHistory.length}</span>
                                </div>
                                <div className={styles.summaryStat}>
                                    <span className={styles.statLabel}>סה"כ תשלומים:</span>
                                    <span className={styles.statValue}>
                                        {paymentHistory.reduce((total, building) =>
                                            total + building.payments.filter(p => p !== null && p.isActualPayment).length, 0
                                        )}
                                    </span>
                                </div>
                                <div className={styles.summaryStat}>
                                    <span className={styles.statLabel}>סה"כ סכום:</span>
                                    <span className={styles.statValue}>
                                        ₪{paymentHistory.reduce((total, building) =>
                                            total + building.payments.reduce((monthTotal, payment) =>
                                                monthTotal + (payment && payment.isActualPayment ? payment.amount : 0), 0
                                            ), 0
                                        ).toLocaleString()}
                                    </span>
                                </div>
                                <div className={styles.summaryStat}>
                                    <span className={styles.statLabel}>חודשים מכוסים:</span>
                                    <span className={styles.statValue}>
                                        {paymentHistory.reduce((total, building) =>
                                            total + building.payments.filter(p => p !== null && p.coveredByPayment).length, 0
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const PaymentModal = ({ isOpen, onClose, payment, onSave, buildings }) => {
    const [formData, setFormData] = useState({
        buildingId: '',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        money: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (payment) {
            setFormData({
                buildingId: payment.buildingId,
                year: payment.year,
                month: payment.month,
                money: payment.money
            });
        } else {
            setFormData({
                buildingId: '',
                year: new Date().getFullYear(),
                month: new Date().getMonth() + 1,
                money: ''
            });
        }
        setErrors({});
    }, [payment, isOpen]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.buildingId) newErrors.buildingId = 'בחר בניין';
        if (!formData.year || formData.year < 2020 || formData.year > 2030) {
            newErrors.year = 'שנה חייבת להיות בין 2020 ל-2030';
        }
        if (!formData.month || formData.month < 1 || formData.month > 12) {
            newErrors.month = 'חודש חייב להיות בין 1 ל-12';
        }
        if (!formData.money || formData.money <= 0) {
            newErrors.money = 'סכום חייב להיות גדול מ-0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const paymentData = {
                buildingId: parseInt(formData.buildingId),
                year: parseInt(formData.year),
                month: parseInt(formData.month),
                money: parseFloat(formData.money)
            };

            // Check if we have a payment with an ID (update) or create new
            if (payment && payment.id) {
                await updateBuildingPayment(payment.id, paymentData);
            } else {
                await createBuildingPayment(paymentData);
            }

            onSave();
            onClose();
        } catch (error) {
            setErrors({ submit: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>{payment ? 'עריכת תשלום' : 'הוספת תשלום חדש'}</h3>
                    <button onClick={onClose} className={styles.modalCloseButton}>×</button>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalForm}>
                    <div className={styles.formGroup}>
                        <label>בניין</label>
                        <select
                            value={formData.buildingId}
                            onChange={(e) => {
                                const selectedBuilding = buildings.find(b => b.buildingId === e.target.value);
                                const suggestedPrice = selectedBuilding ? getSubscriptionPrice(selectedBuilding.numApartments).price : '';
                                setFormData({ 
                                    ...formData, 
                                    buildingId: e.target.value,
                                    money: suggestedPrice
                                });
                            }}
                            className={errors.buildingId ? styles.inputError : ''}
                        >
                            <option value="">בחר בניין</option>
                            {buildings.map(building => {
                                const { price, plan } = getSubscriptionPrice(building.numApartments);
                                return (
                                    <option key={building.buildingId} value={building.buildingId}>
                                        {building.address} {building.addressNumber}, {building.city} ({building.numApartments} דירות - {price} שח)
                                    </option>
                                );
                            })}
                        </select>
                        {errors.buildingId && <span className={styles.errorText}>{errors.buildingId}</span>}
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>שנה</label>
                            <input
                                type="number"
                                value={formData.year}
                                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                                className={errors.year ? styles.inputError : ''}
                                min="2020"
                                max="2030"
                            />
                            {errors.year && <span className={styles.errorText}>{errors.year}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label>חודש</label>
                            <select
                                value={formData.month}
                                onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                                className={errors.month ? styles.inputError : ''}
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={`month-option-${i + 1}`} value={i + 1}>
                                        {new Date(2024, i).toLocaleDateString('he-IL', { month: 'long' })}
                                    </option>
                                ))}
                            </select>
                            {errors.month && <span className={styles.errorText}>{errors.month}</span>}
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>
                            סכום לתשלום (₪)
                            {formData.buildingId && (() => {
                                const selectedBuilding = buildings.find(b => b.buildingId === formData.buildingId);
                                if (selectedBuilding) {
                                    const { price, plan } = getSubscriptionPrice(selectedBuilding.numApartments);
                                    return (
                                        <small className={styles.priceHint}>
                                            מחיר מוצע: {price} שח ({plan})
                                        </small>
                                    );
                                }
                                return null;
                            })()}
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.money}
                            onChange={(e) => setFormData({ ...formData, money: e.target.value })}
                            className={errors.money ? styles.inputError : ''}
                            placeholder="0.00"
                        />
                        {errors.money && <span className={styles.errorText}>{errors.money}</span>}
                    </div>

                    {errors.submit && (
                        <div className={styles.submitError}>{errors.submit}</div>
                    )}

                    <div className={styles.modalFooter}>
                        <button type="button" onClick={onClose} className={styles.cancelButton}>
                            ביטול
                        </button>
                        <button
                            type="submit"
                            className={styles.saveButton}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'שומר...' : (payment ? 'עדכן' : 'הוסף')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// NEW BILLING TABS COMPONENTS

// Building Selector Component for Admin
const BuildingSelector = ({ onBuildingSelect, selectedBuildingId }) => {
    const [buildings, setBuildings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadBuildings = async () => {
            try {
                const result = await fetchBuildings(1, 1000);
                setBuildings(result.items || []);
            } catch (error) {
            } finally {
                setLoading(false);
            }
        };
        loadBuildings();
    }, []);

    return (
        <div className={styles.buildingSelector}>
            <label>בחר בניין:</label>
            <select
                value={selectedBuildingId || ''}
                onChange={(e) => onBuildingSelect(e.target.value)}
                disabled={loading}
                className={styles.buildingSelectorDropdown}
            >
                <option value="">-- בחר בניין --</option>
                {buildings.map(building => (
                    <option key={building.buildingID} value={building.buildingID}>
                        {building.address} {building.addressNumber}, {building.city}
                    </option>
                ))}
            </select>
        </div>
    );
};

// Admin Billing Dashboard Tab
const AdminBillingDashboardTab = () => {
    const [selectedBuildingId, setSelectedBuildingId] = useState(null);

    return (
        <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
                <h3>לוח בקרה חיוב</h3>
                <BuildingSelector
                    onBuildingSelect={setSelectedBuildingId}
                    selectedBuildingId={selectedBuildingId}
                />
            </div>

            {selectedBuildingId ? (
                <div className={styles.billingDashboardWrapper}>
                    <BillingDashboard
                        buildingId={selectedBuildingId}
                        userRole="admin"
                    />
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <CreditCard size={64} className={styles.emptyIcon} />
                    <h4>בחר בניין לניהול חיוב</h4>
                    <p>בחר בניין מהרשימה למעלה כדי לנהל את החיוב והמנויים</p>
                </div>
            )}
        </div>
    );
};

// Admin Payment Form Tab
const AdminPaymentFormTab = () => {
    const [selectedBuildingId, setSelectedBuildingId] = useState(null);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [buildings, setBuildings] = useState([]);

    // Fetch buildings when component mounts
    useEffect(() => {
        const loadBuildings = async () => {
            try {
                const response = await fetchBuildings();
                console.log('AdminPaymentFormTab - Raw API response:', response);
                console.log('Response type:', typeof response);
                console.log('Response is array?', Array.isArray(response));
                
                // Handle different response structures
                let buildingsArray = [];
                if (Array.isArray(response)) {
                    buildingsArray = response;
                } else if (response && Array.isArray(response.buildings)) {
                    buildingsArray = response.buildings;
                } else if (response && response.data && Array.isArray(response.data)) {
                    buildingsArray = response.data;
                } else {
                    console.log('Unexpected response structure, setting empty array');
                }
                
                console.log('Final buildings array:', buildingsArray);
                setBuildings(buildingsArray);
            } catch (error) {
                console.error('Error loading buildings:', error);
                setBuildings([]);
            }
        };
        loadBuildings();
    }, []);

    const handlePaymentNeeded = () => {
        console.log('=== צור תשלום חדש CLICKED ===');
        console.log('Selected Building ID:', selectedBuildingId);
        console.log('Buildings state:', buildings);
        console.log('Buildings is array?', Array.isArray(buildings));
        console.log('Buildings type:', typeof buildings);
        
        // Check if buildings is an array
        if (Array.isArray(buildings)) {
            // Find the selected building
            const selectedBuilding = buildings.find(b => b.buildingId === selectedBuildingId);
            console.log('Selected Building Data:', selectedBuilding);
            
            if (selectedBuilding) {
                console.log('Building numApartments:', selectedBuilding.numApartments, 'type:', typeof selectedBuilding.numApartments);
                const priceInfo = getSubscriptionPrice(selectedBuilding.numApartments);
                console.log('Expected price for this building:', priceInfo);
            } else {
                console.log('Building not found in buildings array');
            }
        } else {
            console.log('Buildings is not an array! Type:', typeof buildings, 'Value:', buildings);
        }
        
        setShowPaymentForm(true);
    };

    const handlePaymentSuccess = (result) => {
        setShowPaymentForm(false);
        // Show success message
        alert('התשלום עובד בהצלחה! מזהה תשלום: ' + (result.paymentAppId || 'לא זמין'));
        // Optionally refresh any data or update UI
    };

    const handleCancelPayment = () => {
        setShowPaymentForm(false);
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
                <h3>טופס תשלום</h3>
                <div className={styles.tabHeaderActions}>
                    <BuildingSelector
                        onBuildingSelect={setSelectedBuildingId}
                        selectedBuildingId={selectedBuildingId}
                    />
                    {selectedBuildingId && !showPaymentForm && (
                        <button
                            onClick={handlePaymentNeeded}
                            className={styles.addButton}
                        >
                            <Plus size={16} />
                            צור תשלום חדש
                        </button>
                    )}
                </div>
            </div>

            {showPaymentForm && selectedBuildingId ? (
                <div className={styles.paymentFormWrapper}>
                    <PaymentAppForm
                        buildingId={selectedBuildingId}
                        onPaymentSuccess={handlePaymentSuccess}
                        onCancel={handleCancelPayment}
                        userRole="admin"
                    />
                </div>
            ) : selectedBuildingId ? (
                <div className={styles.emptyState}>
                    <Receipt size={64} className={styles.emptyIcon} />
                    <h4>לחץ על "צור תשלום חדש"</h4>
                    <p>כדי להתחיל תהליך תשלום עבור הבניין שנבחר</p>
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <FileText size={64} className={styles.emptyIcon} />
                    <h4>בחר בניין לביצוע תשלום</h4>
                    <p>בחר בניין מהרשימה למעלה כדי לבצע תשלום</p>
                </div>
            )}
        </div>
    );
};

// Admin Subscription History Tab
const AdminSubscriptionHistoryTab = () => {
    const [selectedBuildingId, setSelectedBuildingId] = useState(null);

    return (
        <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
                <h3>היסטוריית מנויים</h3>
                <BuildingSelector
                    onBuildingSelect={setSelectedBuildingId}
                    selectedBuildingId={selectedBuildingId}
                />
            </div>

            {selectedBuildingId ? (
                <div className={styles.subscriptionHistoryWrapper}>
                    <PaymentHistory buildingId={selectedBuildingId} />
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <History size={64} className={styles.emptyIcon} />
                    <h4>בחר בניין לצפייה בהיסטוריית מנויים</h4>
                    <p>בחר בניין מהרשימה למעלה כדי לראות את היסטוריית התשלומים והמנויים</p>
                </div>
            )}
        </div>
    );
};

// Backups Tab Component
const BackupsTab = () => {
    const [backups, setBackups] = useState([]);
    const [backupStatus, setBackupStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreatingBackup, setIsCreatingBackup] = useState(false);
    const [isCleaningUp, setIsCleaningUp] = useState(false);
    const [backupName, setBackupName] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    useEffect(() => {
        loadBackupData();
    }, []);

    const loadBackupData = async () => {
        setIsLoading(true);
        setError('');
        try {
            const [backupsData, statusData] = await Promise.all([
                listBackups(),
                getBackupStatus()
            ]);
            setBackups(backupsData.backups || []);
            setBackupStatus(statusData.status);
        } catch (error) {
            setError('שגיאה בטעינת נתוני הגיבויים: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateBackup = async () => {
        setIsCreatingBackup(true);
        setError('');
        setSuccess('');
        try {
            const result = await createBackup(backupName || null);
            setSuccess(`גיבוי נוצר בהצלחה: ${result.backupDetails.fileName}`);
            setBackupName('');
            await loadBackupData();
        } catch (error) {
            setError('שגיאה ביצירת גיבוי: ' + error.message);
        } finally {
            setIsCreatingBackup(false);
        }
    };

    const handleAutoBackup = async () => {
        setIsCreatingBackup(true);
        setError('');
        setSuccess('');
        try {
            const result = await createBackup('AutoBackup');
            setSuccess(`גיבוי אוטומטי נוצר בהצלחה: ${result.backupDetails.fileName}`);
            await loadBackupData();
        } catch (error) {
            setError('שגיאה ביצירת גיבוי אוטומטי: ' + error.message);
        } finally {
            setIsCreatingBackup(false);
        }
    };

    const handleDeleteBackup = async (fileName) => {
        try {
            await deleteBackup(fileName);
            setSuccess(`גיבוי נמחק בהצלחה: ${fileName}`);
            setShowDeleteConfirm(null);
            await loadBackupData();
        } catch (error) {
            setError('שגיאה במחיקת גיבוי: ' + error.message);
        }
    };

    const handleRestoreBackup = async (fileName) => {
        try {
            await restoreBackup(fileName);
            setSuccess(`מסד נתונים שוחזר בהצלחה מגיבוי: ${fileName}`);
            setShowRestoreConfirm(null);
            // Note: After restore, the application might need to be refreshed
            setTimeout(() => {
                alert('השחזור הושלם בהצלחה. העמוד ירוענן כעת.');
                window.location.reload();
            }, 2000);
        } catch (error) {
            setError('שגיאה בשחזור גיבוי: ' + error.message);
        }
    };

    const handleCleanupOld = async () => {
        setIsCleaningUp(true);
        setError('');
        setSuccess('');
        try {
            const result = await cleanupTo14Days();
            setSuccess(`ניקוי הושלם: נמחקו ${result.summary.backupsDeleted} גיבויים ישנים`);
            await loadBackupData();
        } catch (error) {
            setError('שגיאה בניקוי גיבויים: ' + error.message);
        } finally {
            setIsCleaningUp(false);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <RefreshCw className={styles.loadingIcon} />
                <p>טוען נתוני גיבויים...</p>
            </div>
        );
    }

    return (
        <div className={styles.tabContent}>
            <div className={styles.sectionHeader}>
                <h2>ניהול גיבויים</h2>
                <p>צור, נהל ושחזר גיבויי מסד נתונים</p>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <AlertTriangle size={20} />
                    {error}
                </div>
            )}

            {success && (
                <div className={styles.successMessage}>
                    <CheckCircle size={20} />
                    {success}
                </div>
            )}

            {/* Backup Status Card */}
            {backupStatus && (
                <div className={styles.statusCard}>
                    <h3>סטטוס גיבויים</h3>
                    <div className={styles.statusGrid}>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>תיקיית גיבויים:</span>
                            <span className={styles.statusValue}>{backupStatus.backupDirectory}</span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>מסד נתונים:</span>
                            <span className={styles.statusValue}>{backupStatus.databaseName}</span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>סה"כ גיבויים:</span>
                            <span className={styles.statusValue}>{backupStatus.totalBackups}</span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>סה"כ גודל:</span>
                            <span className={styles.statusValue}>{formatFileSize(backupStatus.totalSizeMB)}</span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>מקום פנוי:</span>
                            <span className={styles.statusValue}>{backupStatus.freeSpaceGB} GB</span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>גיבוי אחרון:</span>
                            <span className={styles.statusValue}>
                                {backupStatus.lastBackup ? formatDate(backupStatus.lastBackup) : 'אין גיבויים'}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Backup Section */}
            <div className={styles.actionCard}>
                <h3>יצירת גיבוי חדש</h3>
                <div className={styles.createBackupForm}>
                    <input
                        type="text"
                        placeholder="שם גיבוי (אופציונלי)"
                        value={backupName}
                        onChange={(e) => setBackupName(e.target.value)}
                        className={styles.backupNameInput}
                        disabled={isCreatingBackup}
                    />
                    <button
                        onClick={handleCreateBackup}
                        disabled={isCreatingBackup}
                        className={styles.createBackupButton}
                    >
                        {isCreatingBackup ? (
                            <>
                                <RefreshCw size={16} className={styles.spinning} />
                                יוצר גיבוי...
                            </>
                        ) : (
                            <>
                                <Plus size={16} />
                                צור גיבוי
                            </>
                        )}
                    </button>
                    <button
                        onClick={() => handleAutoBackup()}
                        disabled={isCreatingBackup}
                        className={styles.autoBackupButton}
                    >
                        {isCreatingBackup ? (
                            <>
                                <RefreshCw size={16} className={styles.spinning} />
                                יוצר גיבוי...
                            </>
                        ) : (
                            <>
                                <Database size={16} />
                                גבה עכשיו
                            </>
                        )}
                    </button>
                    <button
                        onClick={handleCleanupOld}
                        disabled={isCleaningUp}
                        className={styles.cleanupButton}
                    >
                        {isCleaningUp ? (
                            <>
                                <RefreshCw size={16} className={styles.spinning} />
                                מנקה...
                            </>
                        ) : (
                            <>
                                <Trash2 size={16} />
                                נקה גיבויים ישנים
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Backups List */}
            <div className={styles.backupsCard}>
                <h3>רשימת גיבויים</h3>
                {backups.length === 0 ? (
                    <div className={styles.noBackups}>
                        <Database size={48} />
                        <p>אין גיבויים זמינים</p>
                        <p>צור גיבוי ראשון כדי להתחיל</p>
                    </div>
                ) : (
                    <div className={styles.backupsTable}>
                        <div className={styles.tableHeader}>
                            <span>שם קובץ</span>
                            <span>גודל</span>
                            <span>תאריך יצירה</span>
                            <span>גיל</span>
                            <span>פעולות</span>
                        </div>
                        {backups.map((backup, index) => (
                            <div key={index} className={styles.tableRow}>
                                <span className={styles.fileName}>{backup.fileName}</span>
                                <span>{formatFileSize(backup.fileSizeMB)}</span>
                                <span>{formatDate(backup.createdAt)}</span>
                                <span>{formatAge(backup.ageInDays)}</span>
                                <div className={styles.actionButtons}>
                                    <button
                                        onClick={() => setShowRestoreConfirm(backup)}
                                        className={styles.restoreButton}
                                        title="שחזר מגיבוי זה"
                                    >
                                        <RefreshCw size={14} />
                                    </button>
                                    <button
                                        onClick={() => setShowDeleteConfirm(backup)}
                                        className={styles.deleteButton}
                                        title="מחק גיבוי"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Restore Confirmation Modal */}
            {showRestoreConfirm && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <h3>אישור שחזור</h3>
                        <p>האם אתה בטוח שברצונך לשחזר את מסד הנתונים מהגיבוי:</p>
                        <p className={styles.fileName}>{showRestoreConfirm.fileName}</p>
                        <p className={styles.warning}>
                            <AlertTriangle size={16} />
                            אזהרה: פעולה זו תחליף את כל הנתונים הנוכחיים!
                        </p>
                        <div className={styles.modalActions}>
                            <button
                                onClick={() => setShowRestoreConfirm(null)}
                                className={styles.cancelButton}
                            >
                                ביטול
                            </button>
                            <button
                                onClick={() => handleRestoreBackup(showRestoreConfirm.fileName)}
                                className={styles.confirmButton}
                            >
                                שחזר
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className={styles.deleteModal}>
                    <div className={styles.deleteModalContent}>
                        <h3>אישור מחיקה</h3>
                        <p>האם אתה בטוח שברצונך למחוק את הגיבוי:</p>
                        <p className={styles.deleteFileName}>{showDeleteConfirm.fileName}</p>
                        <div className={styles.deleteModalActions}>
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className={styles.deleteModalCancel}
                            >
                                ביטול
                            </button>
                            <button
                                onClick={() => handleDeleteBackup(showDeleteConfirm.fileName)}
                                className={styles.deleteModalConfirm}
                            >
                                מחק
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


const AdminDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab') || 'dashboard';
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const [dashboardData, setDashboardData] = useState(null);
    const [buildingPayments, setBuildingPayments] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all', 'paid', 'unpaid'
    const [searchTerm, setSearchTerm] = useState('');
    

    // Updated tabs array with new billing tabs
    const tabs = [
        { id: 'dashboard', label: 'דשבורד כללי', icon: <DollarSign size={18} />, color: '#3b82f6' },
        { id: 'buildings', label: 'ניהול בניינים', icon: <Building2 size={18} />, color: '#10b981' },
        { id: 'suppliers', label: 'ניהול ספקים', icon: <Users size={18} />, color: '#f59e0b' },
        { id: 'users', label: 'ניהול משתמשים', icon: <Users size={18} />, color: '#8b5cf6' },
        { id: 'payment-history', label: 'היסטוריית תשלומים', icon: <History size={18} />, color: '#f97316' },
        { id: 'billing-dashboard', label: 'לוח בקרה חיוב', icon: <CreditCard size={18} />, color: '#06b6d4' },
        { id: 'payment-form', label: 'טופס תשלום', icon: <Receipt size={18} />, color: '#84cc16' },
        { id: 'subscription-history', label: 'היסטוריית מנויים', icon: <FileText size={18} />, color: '#ec4899' },
        { id: 'backups', label: 'גיבויים', icon: <Database size={18} />, color: '#6366f1' }
    ];

    // Update active tab when URL changes
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab') || 'dashboard';
        if (tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams]);

    useEffect(() => {
        if (activeTab === 'dashboard') {
            loadDashboardData();
        }
    }, [activeTab]);


    const loadDashboardData = async () => {
        setIsLoading(true);
        try {
            const [dashboard, payments] = await Promise.all([
                getAdminDashboard(),
                getBuildingPayments()
            ]);

            console.log('=== ADMIN DASHBOARD DEBUG ===');
            console.log('Dashboard data:', dashboard);
            console.log('Payments data:', payments);
            
            // Debug payment statuses
            if (dashboard?.buildingPaymentStatuses) {
                console.log('=== BUILDING PAYMENT STATUSES ===');
                dashboard.buildingPaymentStatuses.forEach(building => {
                    console.log(`Building: ${building.address} ${building.addressNumber}, ${building.city}`);
                    console.log(`  - Has paid this month: ${building.hasPaidThisMonth}`);
                    console.log(`  - Last payment: ${building.lastPaymentDate}`);
                    console.log(`  - Last amount: ${building.lastPaymentAmount}`);
                    console.log('---');
                });
            }
            console.log('Dashboard buildingPaymentStatuses:', dashboard?.buildingPaymentStatuses);

            // Fetch subscriptions for each building individually (workaround)
            console.log('Fetching individual subscriptions for each building...');
            const subscriptionPromises = dashboard.buildingPaymentStatuses.map(building => 
                fetchSubscription(building.buildingId)
                    .then(response => ({
                        buildingId: building.buildingId,
                        subscription: response.success ? response.data : null,
                        error: null
                    }))
                    .catch(error => ({
                        buildingId: building.buildingId,
                        subscription: null,
                        error: error.message
                    }))
            );

            const subscriptionResults = await Promise.all(subscriptionPromises);
            console.log('Subscription results:', subscriptionResults);

            // Fix payment status calculation based on subscription data
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth() + 1;
            const currentYear = currentDate.getFullYear();
            
            console.log('Current date:', currentDate.toISOString());
            console.log('Current month:', currentMonth, 'Current year:', currentYear);

            // Create a map of buildings with active subscriptions based on NextPaymentDue
            const paidBuildingsThisMonth = new Set();
            const buildingPaymentData = {};

            subscriptionResults.forEach((result, index) => {
                console.log(`Subscription result ${index}:`, result);
                
                if (result.subscription && result.subscription.nextPaymentDue) {
                    const subscription = result.subscription;
                    const nextPaymentDate = new Date(subscription.nextPaymentDue);
                    console.log(`Building ${result.buildingId} - NextPaymentDue: ${subscription.nextPaymentDue}, Parsed Date: ${nextPaymentDate.toISOString()}`);
                    
                    // Check if the building is paid (NextPaymentDue is in the future)
                    if (nextPaymentDate > currentDate) {
                        console.log(`✅ Building ${result.buildingId} is paid (NextPaymentDue is in future)`);
                        paidBuildingsThisMonth.add(result.buildingId);
                        buildingPaymentData[result.buildingId] = {
                            amountPaid: subscription.monthlyPrice || 0,
                            paymentDate: subscription.lastPaymentDate || null,
                            nextPaymentDue: subscription.nextPaymentDue,
                            status: subscription.status
                        };
                    } else {
                        console.log(`❌ Building ${result.buildingId} payment is overdue`);
                    }
                } else {
                    console.log(`⚠️ Building ${result.buildingId} - No subscription or NextPaymentDue. Error: ${result.error}`);
                }
            });

            console.log('Paid buildings this month (Set):', Array.from(paidBuildingsThisMonth));
            console.log('Building payment data:', buildingPaymentData);

            // Update dashboard data with corrected payment status
            const correctedBuildingStatuses = dashboard.buildingPaymentStatuses.map((building, index) => {
                const hasPaid = paidBuildingsThisMonth.has(building.buildingId);
                console.log(`Building ${index} - ID: ${building.buildingId}, Address: ${building.address}, Original hasPaidThisMonth: ${building.hasPaidThisMonth}, Corrected hasPaidThisMonth: ${hasPaid}`);
                
                return {
                    ...building,
                    hasPaidThisMonth: hasPaid,
                    amountPaid: buildingPaymentData[building.buildingId]?.amountPaid || 0,
                    paymentDate: buildingPaymentData[building.buildingId]?.paymentDate || null,
                    nextPaymentDue: buildingPaymentData[building.buildingId]?.nextPaymentDue || null
                };
            });

            console.log('Corrected building statuses:', correctedBuildingStatuses);

            // Recalculate statistics based on corrected data
            const buildingsPaid = correctedBuildingStatuses.filter(building => building.hasPaidThisMonth).length;
            const buildingsNotPaid = correctedBuildingStatuses.filter(building => !building.hasPaidThisMonth).length;
            
            console.log('=== RECALCULATED STATISTICS ===');
            console.log('Buildings paid this month:', buildingsPaid);
            console.log('Buildings not paid this month:', buildingsNotPaid);
            console.log('Total buildings:', dashboard.totalBuildings);

            const correctedDashboard = {
                ...dashboard,
                buildingPaymentStatuses: correctedBuildingStatuses,
                buildingsPaidThisMonth: buildingsPaid,
                buildingsNotPaidThisMonth: buildingsNotPaid
            };

            setDashboardData(correctedDashboard);
            setBuildingPayments(payments);

            // Extract unique buildings from payments and dashboard
            const buildingsFromDashboard = correctedBuildingStatuses.map(b => ({
                buildingId: b.buildingId,
                address: b.address,
                city: b.city,
                addressNumber: b.addressNumber
            }));

            const uniqueBuildings = buildingsFromDashboard.reduce((acc, building) => {
                if (!acc.find(b => b.buildingId === building.buildingId)) {
                    acc.push(building);
                }
                return acc;
            }, []);

            setBuildings(uniqueBuildings);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddPayment = () => {
        setSelectedPayment(null);
        setIsModalOpen(true);
    };

    const handleEditBuilding = (building) => {
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();

        if (building.hasPaidThisMonth) {
            // Find the payment for this building for current month
            const payment = buildingPayments.find(p =>
                p.buildingId === building.buildingId &&
                p.month === currentMonth &&
                p.year === currentYear
            );

            // If payment exists with an ID, use it for editing
            // Otherwise, create a new payment object
            if (payment && payment.id) {
                setSelectedPayment(payment);
            } else {
                setSelectedPayment({
                    buildingId: building.buildingId,
                    year: currentYear,
                    month: currentMonth,
                    money: building.amountPaid || 0
                });
            }
        } else {
            // Create new payment for this building
            setSelectedPayment({
                buildingId: building.buildingId,
                year: currentYear,
                month: currentMonth,
                money: 0
            });
        }
        setIsModalOpen(true);
    };

    const handleDeletePayment = async (paymentId) => {
        if (window.confirm('האם אתה בטוח שברצונך למחוק תשלום זה?')) {
            try {
                await deleteBuildingPayment(paymentId);
                loadDashboardData();
            } catch (error) {
            }
        }
    };

    const handleModalSave = () => {
        setIsModalOpen(false);
        loadDashboardData();
    };

    const filteredBuildings = dashboardData?.buildingPaymentStatuses.filter(building => {
        const matchesSearch = searchTerm === '' ||
            building.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
            building.city.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filter === 'all' ||
            (filter === 'paid' && building.hasPaidThisMonth) ||
            (filter === 'unpaid' && !building.hasPaidThisMonth);

        return matchesSearch && matchesFilter;
    }) || [];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'buildings':
                return <BuildingsTab />;
            case 'suppliers':
                return <SuppliersTab />;
            case 'users':
                return <UsersTab />;
            case 'payment-history':
                return <PaymentHistoryTab />;
            case 'billing-dashboard':
                return <AdminBillingDashboardTab />;
            case 'payment-form':
                return <AdminPaymentFormTab />;
            case 'subscription-history':
                return <AdminSubscriptionHistoryTab />;
            case 'backups':
                return <BackupsTab />;
            case 'dashboard':
            default:
                return renderDashboardContent();
        }
    };

    const renderDashboardContent = () => {
        if (isLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <RefreshCw className={styles.loadingIcon} />
                    <p>טוען נתוני ניהול מערכת...</p>
                </div>
            );
        }

        return (
            <>
                {/* Stats Cards */}
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <Building2 size={32} />
                        </div>
                        <div className={styles.statContent}>
                            <h3>סה"כ בניינים</h3>
                            <p className={styles.statValue}>{dashboardData?.totalBuildings || 0}</p>
                        </div>
                    </div>

                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <Users size={32} />
                        </div>
                        <div className={styles.statContent}>
                            <h3>סה"כ משתמשים</h3>
                            <p className={styles.statValue}>{dashboardData?.totalUsers || 0}</p>
                        </div>
                    </div>

                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <DollarSign size={32} />
                        </div>
                        <div className={styles.statContent}>
                            <h3>הכנסות החודש</h3>
                            <p className={styles.statValue}>₪{dashboardData?.totalPaymentsThisMonth?.toLocaleString() || 0}</p>
                        </div>
                    </div>

                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <CheckCircle size={32} />
                        </div>
                        <div className={styles.statContent}>
                            <h3>שילמו החודש</h3>
                            <p className={styles.statValue}>{dashboardData?.buildingsPaidThisMonth || 0}</p>
                            <p className={styles.statSubtext}>
                                מתוך {dashboardData?.totalBuildings || 0} בניינים
                            </p>
                        </div>
                    </div>

                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <AlertTriangle size={32} />
                        </div>
                        <div className={styles.statContent}>
                            <h3>לא שילמו</h3>
                            <p className={styles.statValue}>{dashboardData?.buildingsNotPaidThisMonth || 0}</p>
                            <p className={styles.statSubtext}>
                                {dashboardData?.totalBuildings > 0
                                    ? `${Math.round((dashboardData.buildingsNotPaidThisMonth / dashboardData.totalBuildings) * 100)}% מהבניינים`
                                    : '0%'
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* Buildings Status Section */}
                <div className={styles.buildingsSection}>
                    <div className={styles.sectionHeader}>
                        <h2>סטטוס תשלומי בניינים - {new Date().toLocaleDateString('he-IL', { month: 'long', year: 'numeric' })}</h2>

                        <div className={styles.sectionControls}>
                            <div className={styles.searchBox}>
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="חפש בניין..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className={styles.filterButtons}>
                                <button
                                    className={`${styles.filterButton} ${filter === 'all' ? styles.active : ''}`}
                                    onClick={() => setFilter('all')}
                                >
                                    הכל ({dashboardData?.totalBuildings || 0})
                                </button>
                                <button
                                    className={`${styles.filterButton} ${styles.paidFilter} ${filter === 'paid' ? styles.active : ''}`}
                                    onClick={() => setFilter('paid')}
                                >
                                    שילמו ({dashboardData?.buildingsPaidThisMonth || 0})
                                </button>
                                <button
                                    className={`${styles.filterButton} ${styles.unpaidFilter} ${filter === 'unpaid' ? styles.active : ''}`}
                                    onClick={() => setFilter('unpaid')}
                                >
                                    לא שילמו ({dashboardData?.buildingsNotPaidThisMonth || 0})
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Buildings Table */}
                    <div className={styles.tableContainer}>
                        <table className={styles.buildingsTable}>
                            <thead>
                                <tr>
                                    <th>בניין</th>
                                    <th>עיר</th>
                                    <th>סטטוס תשלום</th>
                                    <th>תקופת ניסיון</th>
                                    <th>סטטוס</th>
                                    <th>סכום</th>
                                    <th>תאריך תשלום</th>
                                    <th>תאריך תשלום הבא</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBuildings.map(building => (
                                    <tr
                                        key={building.buildingId}
                                        className={building.hasPaidThisMonth ? styles.paidRow : styles.unpaidRow}
                                    >
                                        <td className={styles.buildingAddressCell}>
                                            <div className={styles.buildingDetails}>
                                                <strong>{building.address} {building.addressNumber}</strong>
                                            </div>
                                        </td>
                                        <td className={styles.cityCell}>
                                            {building.city}
                                        </td>
                                        <td className={styles.statusCell}>
                                            <div className={styles.statusIndicator}>
                                                <div className={`${styles.statusIcon} ${building.hasPaidThisMonth ? styles.paidIcon : styles.unpaidIcon}`}>
                                                    {building.hasPaidThisMonth ? <CheckCircle size={20} /> : <XCircle size={20} />}
                                                </div>
                                                <span className={styles.statusText}>
                                                    {building.hasPaidThisMonth ? 'שולם' : 'לא שולם'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className={styles.dateCell}>
                                            {building.trialEndDate ? (
                                                (() => {
                                                    const date = new Date(building.trialEndDate);
                                                    if (isNaN(date.getTime())) return '-';
                                                    
                                                    // Check if date is DateTime.MinValue (01/01/1 or similar)
                                                    if (date.getFullYear() <= 1970) return '-';
                                                    
                                                    const day = String(date.getDate()).padStart(2, '0');
                                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                                    const year = date.getFullYear();
                                                    return `${day}/${month}/${year}`;
                                                })()
                                            ) : '-'}
                                        </td>
                                        <td className={styles.statusCell}>
                                            <div className={styles.subscriptionStatusIndicator}>
                                                <span className={`${styles.subscriptionStatusText} ${building.subscriptionStatus === 'Active' ? styles.activeSubscription : 
                                                    building.subscriptionStatus === 'Expired' ? styles.expiredSubscription :
                                                    building.subscriptionStatus === 'Trial' ? styles.trialSubscription : styles.unknownSubscription}`}>
                                                    {building.subscriptionStatus || 'Unknown'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className={styles.amountCell}>
                                            {building.hasPaidThisMonth ? (
                                                <span className={styles.paidAmount}>
                                                    ₪{building.amountPaid?.toLocaleString()}
                                                </span>
                                            ) : (
                                                <span className={styles.unpaidAmount}>-</span>
                                            )}
                                        </td>
                                        <td className={styles.dateCell}>
                                            {building.hasPaidThisMonth && building.paymentDate ? (
                                                (() => {
                                                    const date = new Date(building.paymentDate);
                                                    if (isNaN(date.getTime())) return '-';
                                                    const day = String(date.getDate()).padStart(2, '0');
                                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                                    const year = date.getFullYear();
                                                    return `${day}/${month}/${year}`;
                                                })()
                                            ) : '-'}
                                        </td>
                                        <td className={styles.dateCell}>
                                            {building.nextPaymentDue ? (
                                                (() => {
                                                    const date = new Date(building.nextPaymentDue);
                                                    if (isNaN(date.getTime())) return '-';
                                                    const day = String(date.getDate()).padStart(2, '0');
                                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                                    const year = date.getFullYear();
                                                    return `${day}/${month}/${year}`;
                                                })()
                                            ) : '-'}
                                        </td>
                                        <td className={styles.actionsCell}>
                                            <button
                                                onClick={() => handleEditBuilding(building)}
                                                className={`${styles.actionButton} ${building.hasPaidThisMonth ? styles.editPaymentButton : styles.addPaymentButton}`}
                                                title={building.hasPaidThisMonth ? 'ערוך תשלום' : 'הוסף תשלום'}
                                            >
                                                {building.hasPaidThisMonth ? <Edit size={16} /> : <Plus size={16} />}
                                                {building.hasPaidThisMonth ? 'ערוך' : 'הוסף תשלום'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredBuildings.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className={styles.emptyCell}>
                                            לא נמצאו בניינים התואמים את החיפוש
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {filteredBuildings.length === 0 && (
                        <div className={styles.emptyState}>
                            <p>לא נמצאו בניינים התואמים את החיפוש</p>
                        </div>
                    )}
                </div>


                {/* Recent Payments Table */}
                <div className={styles.recentPayments}>
                    <h2>תשלומים אחרונים</h2>
                    <div className={styles.tableContainer}>
                        <table className={styles.paymentsTable}>
                            <thead>
                                <tr>
                                    <th>בניין</th>
                                    <th>חודש/שנה</th>
                                    <th>סכום</th>
                                    <th>תאריך יצירה</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buildingPayments.slice(0, 10).map(payment => (
                                    <tr key={payment.id}>
                                        <td>
                                            <div>
                                                <strong>{payment.buildingAddress}</strong>
                                                <br />
                                                <small>{payment.buildingCity}</small>
                                            </div>
                                        </td>
                                        <td>{payment.monthName} {payment.year}</td>
                                        <td>₪{payment.money?.toLocaleString()}</td>
                                        <td>
                                            {(() => {
                                                const date = new Date(payment.createdDate);
                                                if (isNaN(date.getTime())) return '-';
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                return `${day}/${month}/${year}`;
                                            })()}
                                        </td>
                                        <td>
                                            <div className={styles.actionButtons}>
                                                <button
                                                    onClick={() => {
                                                        setSelectedPayment(payment);
                                                        setIsModalOpen(true);
                                                    }}
                                                    className={styles.editActionButton}
                                                    title="ערוך"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePayment(payment.id)}
                                                    className={styles.deleteActionButton}
                                                    title="מחק"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Payment Modal */}
                <PaymentModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    payment={selectedPayment}
                    onSave={handleModalSave}
                    buildings={buildings}
                />
            </>
        );
    };

    return (
        <div className={styles.adminDashboard}>
            {/* Header */}
            <div className={styles.dashboardHeader}>
                <div className={styles.headerLeft}>
                    <h1>ניהול מערכת</h1>
                    <p>לוח בקרה מנהלים - תצוגה כוללת של המערכת</p>
                </div>
                <div className={styles.headerRight}>
                    {activeTab === 'dashboard' && (
                        <>
                            <button
                                onClick={loadDashboardData}
                                className={styles.refreshButton}
                                disabled={isLoading}
                            >
                                <RefreshCw size={16} className={isLoading ? styles.rotating : ''} />
                                רענן נתונים
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className={styles.contentContainer}>
                {renderTabContent()}
            </div>

        </div>
    );
};

export default AdminDashboard;